<template>
  <div class="coupon-provider-manage">
    <a-card title="쿠폰 공급사 관리" :bordered="false">
      <template #extra>
        <a-button type="primary" class="yellow-button" @click="openCreateModal">
          <template #icon><PlusOutlined /></template>
          신규 등록
        </a-button>
      </template>

      <!-- Search Filters -->
      <div class="filter-section">
        <a-row :gutter="16">
          <a-col :span="8">
            <a-select
              v-model:value="searchParams.provider"
              placeholder="공급사 선택"
              allowClear
              style="width: 100%"
              @change="handleSearch"
            >
              <a-select-option value="giftiel">기프티엘 (Giftiel)</a-select-option>
              <a-select-option value="sendb">센드비 (Sendb)</a-select-option>
            </a-select>
          </a-col>
          <a-col :span="8">
            <a-select
              v-model:value="searchParams.isActive"
              placeholder="상태 선택"
              allowClear
              style="width: 100%"
              @change="handleSearch"
            >
              <a-select-option :value="true">활성</a-select-option>
              <a-select-option :value="false">비활성</a-select-option>
            </a-select>
          </a-col>
          <a-col :span="8">
            <a-button type="primary" @click="handleSearch">검색</a-button>
            <a-button style="margin-left: 8px" @click="resetSearch">초기화</a-button>
          </a-col>
        </a-row>
      </div>

      <!-- Data Table -->
      <a-table
        :columns="columns"
        :data-source="contracts"
        :pagination="pagination"
        :loading="loading"
        rowKey="_id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'provider'">
            <a-tag :color="record.provider === 'giftiel' ? 'blue' : 'green'">
              {{ record.provider === 'giftiel' ? '기프티엘' : '센드비' }}
            </a-tag>
          </template>

          <template v-if="column.key === 'contractorInfo'">
            <div>
              <strong>{{ record.contractorName }}</strong>
              <br />
              <span class="text-muted">{{ record.contractorCode }}</span>
            </div>
          </template>

          <template v-if="column.key === 'credentials'">
            <a-tooltip>
              <template #title>
                <div v-if="record.provider === 'giftiel'">
                  CI Code: {{ record.credentials?.ciCode ? '****' : '-' }}
                </div>
                <div v-else>
                  API Key: {{ record.credentials?.apiKey ? '****' : '-' }}
                </div>
              </template>
              <a-tag color="purple">인증정보 설정됨</a-tag>
            </a-tooltip>
          </template>

          <template v-if="column.key === 'isActive'">
            <a-switch
              :checked="record.isActive"
              @change="(checked) => handleStatusChange(record, checked)"
              checked-children="활성"
              un-checked-children="비활성"
            />
          </template>

          <template v-if="column.key === 'actions'">
            <a-space>
              <a-button type="link" size="small" @click="openEditModal(record)">
                수정
              </a-button>
              <a-popconfirm
                title="정말 삭제하시겠습니까?"
                ok-text="삭제"
                cancel-text="취소"
                @confirm="handleDelete(record._id)"
              >
                <a-button type="link" size="small" danger>삭제</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- Create/Edit Modal -->
    <CouponProviderModal
      v-model:visible="modalVisible"
      :mode="modalMode"
      :contract="selectedContract"
      @success="handleModalSuccess"
    />
  </div>
</template>

<script>
import { PlusOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import couponProviderApis from './api';
import CouponProviderModal from './CouponProviderModal.vue';

export default {
  name: 'CouponProviderManage',
  components: {
    PlusOutlined,
    CouponProviderModal
  },
  data() {
    return {
      api: null,
      loading: false,
      contracts: [],
      searchParams: {
        provider: undefined,
        isActive: undefined
      },
      pagination: {
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `총 ${total}건`
      },
      columns: [
        {
          title: '공급사',
          key: 'provider',
          width: 120
        },
        {
          title: '표시명',
          dataIndex: 'displayName',
          key: 'displayName',
          width: 200
        },
        {
          title: '계약사 정보',
          key: 'contractorInfo',
          width: 180
        },
        {
          title: '인증정보',
          key: 'credentials',
          width: 120
        },
        {
          title: '정렬순서',
          dataIndex: 'sortOrder',
          key: 'sortOrder',
          width: 100,
          align: 'center'
        },
        {
          title: '상태',
          key: 'isActive',
          width: 100,
          align: 'center'
        },
        {
          title: '관리',
          key: 'actions',
          width: 150,
          align: 'center'
        }
      ],
      modalVisible: false,
      modalMode: 'create',
      selectedContract: null
    };
  },
  created() {
    this.api = couponProviderApis(this.$apiInstance, this.$store);
    this.fetchContracts();
  },
  methods: {
    fetchContracts() {
      this.loading = true;
      const params = { ...this.searchParams };

      this.api.getContracts(
        params,
        (response) => {
          // Response is an array directly
          this.contracts = response.data || [];
          this.loading = false;
        },
        (error) => {
          message.error('데이터 조회 실패: ' + (error.response?.data?.message || error.message));
          this.loading = false;
        }
      );
    },

    handleSearch() {
      this.fetchContracts();
    },

    resetSearch() {
      this.searchParams = {
        provider: undefined,
        isActive: undefined
      };
      this.fetchContracts();
    },

    handleStatusChange(record, checked) {
      const apiMethod = checked ? this.api.activateContract : this.api.deactivateContract;
      const statusText = checked ? '활성화' : '비활성화';

      apiMethod(
        record._id,
        () => {
          message.success(`${statusText} 되었습니다.`);
          this.fetchContracts();
        },
        (error) => {
          message.error(`${statusText} 실패: ` + (error.response?.data?.message || error.message));
        }
      );
    },

    handleDelete(id) {
      this.api.deleteContract(
        id,
        () => {
          message.success('삭제되었습니다.');
          this.fetchContracts();
        },
        (error) => {
          message.error('삭제 실패: ' + (error.response?.data?.message || error.message));
        }
      );
    },

    openCreateModal() {
      this.modalMode = 'create';
      this.selectedContract = null;
      this.modalVisible = true;
    },

    openEditModal(record) {
      this.modalMode = 'edit';
      this.selectedContract = { ...record };
      this.modalVisible = true;
    },

    handleModalSuccess() {
      this.modalVisible = false;
      this.fetchContracts();
    }
  }
};
</script>

<style scoped>
.coupon-provider-manage {
  padding: 24px;
}

.filter-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 4px;
}

.text-muted {
  color: #999;
  font-size: 12px;
}

.yellow-button {
  background-color: #faad14 !important;
  border-color: #faad14 !important;
}

.yellow-button:hover {
  background-color: #ffc53d !important;
  border-color: #ffc53d !important;
}
</style>
